/**
 * RhVisitesMedicalesPanel — suivi des visites médicales & entretien
 * professionnel (item 5.5).
 *
 * ─── CE QUE CET ÉCRAN SERT À FAIRE ──────────────────────────────────────────
 *
 * Répondre à une question : **qui faut-il convoquer ?**
 *
 * Il ouvre donc sur ce qui est en retard ou proche de l'échéance, comme l'écran
 * des habilitations — pas sur une liste alphabétique où l'urgence se noierait.
 *
 * ─── CE QU'IL NE CONTIENT PAS, ET NE DOIT JAMAIS CONTENIR ───────────────────
 *
 * Aucune donnée de santé : date + type de convocation, rien d'autre. Pas d'avis
 * d'aptitude, pas de restriction, pas de motif. Ces informations relèvent du
 * médecin du travail et de l'article 9 du RGPD — les faire entrer ici
 * changerait tout le régime applicable à la base.
 *
 * Le champ « note » est explicitement une note d'ORGANISATION (convocation
 * envoyée, lieu du rendez-vous), et son libellé le rappelle à la saisie.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, Badge, Alert, Button } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type { RhVisiteMedicale, RhSuiviReglages, AdminUserListItem } from '@roronoa-collectivites/shared-types';
import { Stethoscope, Plus, Trash2, Settings2, RefreshCw, Info } from 'lucide-react';

const TYPES: Array<{ value: RhVisiteMedicale['type']; label: string }> = [
  { value: 'embauche', label: 'Embauche' },
  { value: 'periodique', label: 'Périodique' },
  { value: 'reprise', label: 'Reprise' },
];

/** Jours restants avant l'échéance (négatif = dépassée). */
function joursRestants(iso: string | null): number | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const cible = new Date(y!, (m ?? 1) - 1, d ?? 1);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((cible.getTime() - today.getTime()) / 86_400_000);
}

function EtatBadge({ v }: { v: RhVisiteMedicale }) {
  const j = joursRestants(v.dateProchaine);
  if (j === null) return <Badge variant="neutral">Sans échéance</Badge>;
  if (j < 0) return <Badge variant="urgent">En retard de {Math.abs(j)} j</Badge>;
  if (j <= 60) return <Badge variant="warning">Dans {j} j</Badge>;
  return <Badge variant="success">Dans {j} j</Badge>;
}

const fr = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : '—');

export function RhVisitesMedicalesPanel() {
  const { confirm, dialogElement } = useConfirmDialog();
  const [items, setItems] = useState<RhVisiteMedicale[]>([]);
  const [agents, setAgents] = useState<AdminUserListItem[]>([]);
  const [reglages, setReglages] = useState<RhSuiviReglages | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [ouvrirForm, setOuvrirForm] = useState(false);
  const [ouvrirReglages, setOuvrirReglages] = useState(false);
  const [saving, setSaving] = useState(false);

  const [agentUserId, setAgentUserId] = useState('');
  const [type, setType] = useState<RhVisiteMedicale['type']>('periodique');
  const [dateVisite, setDateVisite] = useState('');
  const [dateProchaine, setDateProchaine] = useState('');
  const [note, setNote] = useState('');

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [v, r] = await Promise.all([
        api.rh.visitesMedicales(),
        api.rh.suiviReglages(),
      ]);
      setItems(v); setReglages(r);
      // La liste d'agents n'est pas critique : son échec ne doit pas vider
      // l'écran principal.
      try { setAgents((await api.admin.users.list()).items.filter((u: AdminUserListItem) => u.actif)); } catch { /* ignoré */ }
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  async function enregistrer() {
    if (!agentUserId) { setError('Choisissez un agent'); return; }
    setSaving(true); setError(null);
    try {
      await api.rh.creerVisiteMedicale({
        agentUserId, type,
        dateVisite: dateVisite || null,
        dateProchaine: dateProchaine || null,
        noteOrganisation: note || null,
      });
      setMsg('Visite enregistrée.');
      setOuvrirForm(false);
      setAgentUserId(''); setDateVisite(''); setDateProchaine(''); setNote('');
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Enregistrement impossible');
    } finally { setSaving(false); }
  }

  async function supprimer(v: RhVisiteMedicale) {
    const ok = await confirm({
      title: 'Supprimer cette visite ?',
      description: `La convocation de ${v.agentNom} et son rappel seront supprimés.`,
      confirmLabel: 'Supprimer', danger: true,
    });
    if (!ok) return;
    try {
      await api.rh.supprimerVisiteMedicale(v.id);
      await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Suppression impossible'); }
  }

  async function synchroniser() {
    setSaving(true); setError(null);
    try {
      const r = await api.rh.synchroniserEntretiensPro();
      setMsg(`${r.programmes} rappel(s) d'entretien programmé(s), ${r.alertes} alerte(s) d'arrêt long.`);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Synchronisation impossible');
    } finally { setSaving(false); }
  }

  // Les plus urgents d'abord : sans échéance en dernier.
  const tries = [...items].sort((a, b) => {
    const ja = joursRestants(a.dateProchaine), jb = joursRestants(b.dateProchaine);
    if (ja === null) return 1;
    if (jb === null) return -1;
    return ja - jb;
  });

  const champ = 'h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink';
  const label = 'mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle';

  return (
    <div className="space-y-4">
      {dialogElement}
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      <Card variant="glass" padded>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <Stethoscope className="h-4 w-4 text-rh-fg" aria-hidden="true" />
            Visites médicales
          </h3>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOuvrirReglages((o) => !o)}>
              <Settings2 className="h-4 w-4" aria-hidden="true" /> Réglages
            </Button>
            <Button variant="secondary" size="sm" loading={saving} onClick={() => void synchroniser()}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" /> Entretiens annuels
            </Button>
            <Button variant="primary" size="sm" onClick={() => setOuvrirForm((o) => !o)}>
              <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter
            </Button>
          </div>
        </div>

        <p className="mb-3 flex items-start gap-1.5 text-xs text-ink-muted">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            Ce suivi ne contient <strong>aucune information médicale</strong> : uniquement la date
            et le type de convocation. L'avis d'aptitude reste chez le médecin du travail.
          </span>
        </p>

        {ouvrirReglages && reglages && (
          <div className="mb-4 grid gap-3 rounded-r-md border border-border bg-surface/40 p-3 sm:grid-cols-2">
            <div>
              <label className={label}>Périodicité du suivi (mois)</label>
              <input type="number" min={1} max={60} value={reglages.periodiciteMois}
                onChange={(e) => setReglages({ ...reglages, periodiciteMois: Number(e.target.value) })}
                className={champ} />
              <p className="mt-1 text-xs text-ink-subtle">24 mois = suivi de droit commun.</p>
            </div>
            <div>
              <label className={label}>Seuil d'arrêt long (jours/an)</label>
              <input type="number" min={1} max={365} value={reglages.seuilArretJours}
                onChange={(e) => setReglages({ ...reglages, seuilArretJours: Number(e.target.value) })}
                className={champ} />
              <p className="mt-1 text-xs text-ink-subtle">
                Au-delà, la RH est alertée que l'entretien annuel sera difficile à organiser.
              </p>
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button variant="secondary" size="sm" loading={saving}
                onClick={async () => {
                  setSaving(true);
                  try { setReglages(await api.rh.setSuiviReglages(reglages)); setMsg('Réglages enregistrés.'); }
                  catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
                  finally { setSaving(false); }
                }}>
                Enregistrer les réglages
              </Button>
            </div>
          </div>
        )}

        {ouvrirForm && (
          <div className="mb-4 grid gap-3 rounded-r-md border border-border bg-surface/40 p-3 sm:grid-cols-2">
            <div>
              <label className={label}>Agent *</label>
              <select value={agentUserId} onChange={(e) => setAgentUserId(e.target.value)} className={champ}>
                <option value="">— choisir —</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.prenom} {a.nom}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>Type de visite</label>
              <select value={type} onChange={(e) => setType(e.target.value as RhVisiteMedicale['type'])} className={champ}>
                {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Date de la visite</label>
              <input type="date" value={dateVisite} onChange={(e) => setDateVisite(e.target.value)} className={champ} />
            </div>
            <div>
              <label className={label}>Prochaine échéance</label>
              <input type="date" value={dateProchaine} onChange={(e) => setDateProchaine(e.target.value)} className={champ} />
              <p className="mt-1 text-xs text-ink-subtle">
                Calculée automatiquement si vide{reglages ? ` (+${reglages.periodiciteMois} mois)` : ''}.
              </p>
            </div>
            <div className="sm:col-span-2">
              <label className={label}>Note d'organisation</label>
              <input value={note} onChange={(e) => setNote(e.target.value)} className={champ}
                placeholder="Convocation envoyée, lieu du rendez-vous…" maxLength={500} />
              <p className="mt-1 text-xs text-ink-subtle">
                Organisation uniquement — aucune information médicale.
              </p>
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setOuvrirForm(false)}>Annuler</Button>
              <Button variant="primary" size="sm" loading={saving} onClick={() => void enregistrer()}>
                Enregistrer
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-ink-subtle">Chargement…</p>
        ) : tries.length === 0 ? (
          <p className="text-sm text-ink-subtle">Aucune visite enregistrée.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-ink-subtle">
                  <th className="py-2 font-medium">Agent</th>
                  <th className="py-2 font-medium">Type</th>
                  <th className="py-2 font-medium">Dernière</th>
                  <th className="py-2 font-medium">Prochaine</th>
                  <th className="py-2 font-medium">État</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {tries.map((v) => (
                  <tr key={v.id} className="border-b border-border/60 last:border-0">
                    <td className="py-2 text-ink">{v.agentNom}</td>
                    <td className="py-2 text-ink-muted">
                      {TYPES.find((t) => t.value === v.type)?.label ?? v.type}
                    </td>
                    <td className="py-2 tabular-nums text-ink-muted">{fr(v.dateVisite)}</td>
                    <td className="py-2 tabular-nums text-ink-muted">{fr(v.dateProchaine)}</td>
                    <td className="py-2"><EtatBadge v={v} /></td>
                    <td className="py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => void supprimer(v)} aria-label={`Supprimer la visite de ${v.agentNom}`}>
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
